import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Save, Eye, Image as ImageIcon, X, AlertTriangle,
  Plus, Trash2, ArrowUp, ArrowDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import RichTextEditor from "@/admin/components/RichTextEditor";
import MediaPicker from "@/admin/components/MediaPicker";
import { FEATURE_ICONS, FEATURE_ICON_NAMES, getFeatureIcon } from "@/data/featureIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FEATURE = {
  slug: "",
  name: "",
  short_desc: "",
  group_slug: "",
  icon: "Sparkles",
  order: 100,
  published: true,
  has_detail_page: false,
  hero_image: null,
  body_html: "",
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  steps_heading: "",
  steps: [],
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

// Normalises a URL for comparison: trims whitespace, drops trailing slash,
// strips query string / hash. Case-sensitive on path (URLs are case-sensitive
// per RFC 3986, and CDN keys often are too).
function normalizeImgUrl(url) {
  if (!url) return "";
  let u = String(url).trim();
  // Strip surrounding quotes if any leaked in.
  u = u.replace(/^["']|["']$/g, "");
  // Drop query + hash.
  u = u.split("#")[0].split("?")[0];
  // Drop trailing slash.
  u = u.replace(/\/+$/, "");
  return u;
}

// Extracts all <img src="..."> values from a raw HTML string, without a full
// HTML parse. Handles single or double quotes; ignores markup inside comments
// (best-effort - the CMS body isn't user-hostile so this is fine).
function extractImgSrcs(html) {
  if (!html || typeof html !== "string") return [];
  const out = [];
  const re = /<img[^>]*\ssrc\s*=\s*(?:"([^"]+)"|'([^']+)')/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    out.push(m[1] || m[2]);
  }
  return out;
}

export default function AdminFeatureEditPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug || slug === "new";

  const [form, setForm] = useState(EMPTY_FEATURE);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Media picker target. `null` = closed. Otherwise a discriminated
  // object routes the onPick to the right form field.
  //   { type: "hero" }
  //   { type: "step", index: N }
  const [mediaTarget, setMediaTarget] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/feature-groups`);
        setGroups(data || []);
        if (isNew && !form.group_slug && data && data[0]) {
          setForm((p) => ({ ...p, group_slug: data[0].slug }));
        }
      } catch {
        toast.error("Could not load groups.");
      }
    })();
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/features/${slug}`);
        setForm({
          slug: data.slug,
          name: data.name || "",
          short_desc: data.short_desc || "",
          group_slug: data.group_slug || "",
          icon: data.icon || "Sparkles",
          order: data.order ?? 0,
          published: data.published !== false,
          has_detail_page: !!data.has_detail_page,
          hero_image: data.hero_image || null,
          body_html: data.body_html || "",
          seo_title: data.seo_title || "",
          seo_description: data.seo_description || "",
          seo_keywords: data.seo_keywords || "",
          steps_heading: data.steps_heading || "",
          steps: Array.isArray(data.steps) ? data.steps : [],
        });
      } catch {
        toast.error("Feature not found.");
        navigate("/admin/features");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Warn if the hero image URL is *also* embedded inside the body HTML.
  // This is a very common client mistake (they upload the hero once,
  // then paste the same image into the rich-text body) and creates a
  // duplicate stack on the public feature detail page.
  const duplicateImage = useMemo(() => {
    if (!form.has_detail_page) return null;
    const hero = normalizeImgUrl(form.hero_image);
    if (!hero) return null;
    const bodySrcs = extractImgSrcs(form.body_html).map(normalizeImgUrl);
    return bodySrcs.includes(hero) ? hero : null;
  }, [form.hero_image, form.body_html, form.has_detail_page]);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    if (isNew && !form.slug.trim()) return toast.error("Slug is required.");
    if (!form.group_slug) return toast.error("Please pick a group.");
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form, slug: form.slug || slugify(form.name) };
        await axios.post(`${API}/admin/features`, payload);
        toast.success("Feature created.");
        navigate(`/admin/features/${payload.slug}`);
      } else {
        // Don't send slug in a PATCH (it's the URL key).
        const patch = { ...form };
        delete patch.slug;
        await axios.patch(`${API}/admin/features/${slug}`, patch);
        toast.success("Saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-sm text-slate-400" data-testid="admin-feature-edit-loading">
        Loading…
      </div>
    );
  }

  const IconPreview = getFeatureIcon(form.icon);

  return (
    <div data-testid="admin-feature-edit-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="px-5 lg:px-8 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between sticky top-0 bg-white z-10">
        <div className="flex items-center gap-3">
          <Link to="/admin/features" data-testid="admin-feature-edit-back" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-base font-bold text-slate-900">
              {isNew ? "New Feature" : form.name || "Edit Feature"}
            </h1>
            <p className="text-[11px] text-slate-400 font-mono mt-0.5">
              {isNew ? "Draft" : form.has_detail_page ? `/features/${form.slug}` : `Card only - no detail page`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && form.has_detail_page && (
            <Link to={`/features/${form.slug}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="admin-feature-edit-preview" className="rounded-lg text-xs">
                <Eye className="h-3.5 w-3.5 mr-1" /> Preview
              </Button>
            </Link>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-feature-edit-save"
            className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs h-9"
          >
            <Save className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : isNew ? "Create feature" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Feature Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => {
                const name = e.target.value;
                update({ name, slug: isNew && !form.slug ? slugify(name) : form.slug });
              }}
              placeholder="e.g. AI Reporting"
              data-testid="admin-feature-edit-name"
              className="mt-1.5 h-10"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">URL Slug *</Label>
            <Input
              value={form.slug}
              onChange={(e) => update({ slug: slugify(e.target.value) })}
              disabled={!isNew}
              placeholder="e.g. ai-reporting"
              data-testid="admin-feature-edit-slug"
              className="mt-1.5 h-10 font-mono text-xs"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Card only if detail page is off. Becomes <span className="font-mono">/features/{form.slug || "<slug>"}</span> when detail page is on. Cannot be changed after creation.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Short description (shown on the /features card)</Label>
            <Textarea
              value={form.short_desc}
              onChange={(e) => update({ short_desc: e.target.value })}
              rows={2}
              placeholder="One-liner shown under the icon on the /features card."
              data-testid="admin-feature-edit-shortdesc"
              className="mt-1.5"
            />
          </div>

          {/* Detail page section */}
          <div className="border-t border-slate-200 pt-5">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.has_detail_page}
                onChange={(e) => update({ has_detail_page: e.target.checked })}
                data-testid="admin-feature-edit-hasdetail"
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              <span className="text-sm font-semibold text-slate-800">Has a detail page</span>
              <span className="text-xs text-slate-400">→ /features/{form.slug || "<slug>"}</span>
            </label>
            <p className="text-[11px] text-slate-400 mt-1 ml-6">
              When ON, the card on /features becomes clickable and opens a dedicated page with the content below.
            </p>
          </div>

          {form.has_detail_page && (
            <>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Hero image (optional)</Label>
                <div className="mt-1.5 space-y-2" data-testid="admin-feature-edit-hero">
                  {form.hero_image && (
                    <div className="rounded-lg overflow-hidden border border-slate-200 relative">
                      <img src={form.hero_image} alt="" className="w-full max-h-56 object-cover" />
                      <button
                        type="button"
                        onClick={() => update({ hero_image: null })}
                        className="absolute top-2 right-2 bg-white/90 text-slate-600 hover:text-red-500 rounded-full p-1 shadow"
                        aria-label="Remove"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {duplicateImage && (
                    <div
                      data-testid="admin-feature-edit-hero-duplicate-warning"
                      className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
                      role="alert"
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                      <div className="leading-snug">
                        <strong className="font-semibold">Duplicate image detected.</strong>{" "}
                        This hero image is also embedded inside the detail page
                        body below, so it will render twice on the public page.
                        Either clear the hero image or remove the matching
                        <span className="mx-1 font-mono">&lt;img&gt;</span>
                        from the body.
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setMediaTarget({ type: "hero" })}
                      className="h-9 text-xs rounded-lg shrink-0"
                    >
                      <ImageIcon className="h-3.5 w-3.5 mr-1" />
                      {form.hero_image ? "Change" : "Pick from library"}
                    </Button>
                    <Input
                      value={form.hero_image || ""}
                      onChange={(e) => update({ hero_image: e.target.value || null })}
                      placeholder="…or paste URL"
                      className="text-xs font-mono h-9"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700">Detail page body</Label>
                <p className="text-[11px] text-slate-400 mt-0.5 mb-2">
                  Full rich-text - headings, paragraphs, images, videos, quotes, bullets, links. Renders below the hero on the detail page.
                </p>
                <RichTextEditor
                  value={form.body_html}
                  onChange={(html) => update({ body_html: html })}
                  placeholder="Write the feature detail page…"
                  testId="admin-feature-edit-body"
                />
              </div>

              {/* Step-by-step walkthrough */}
              <div
                className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50"
                data-testid="admin-feature-edit-steps"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">
                      Step walkthrough
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Optional. Each step renders as a row with text on the left and a screenshot on the right. Numbers auto-generate (&ldquo;Step 1&rdquo;, &ldquo;Step 2&rdquo;...) by list order.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() =>
                      update({
                        steps: [
                          ...form.steps,
                          { title: "", description: "", image: null },
                        ],
                      })
                    }
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-lg text-xs shrink-0"
                    data-testid="admin-feature-edit-step-add"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add step
                  </Button>
                </div>

                <div>
                  <Label className="text-xs text-slate-600">Section heading (optional)</Label>
                  <Input
                    value={form.steps_heading}
                    onChange={(e) => update({ steps_heading: e.target.value })}
                    placeholder='e.g. "Build a presentation in 10 steps"'
                    data-testid="admin-feature-edit-steps-heading"
                    className="mt-1 h-9 text-sm"
                  />
                </div>

                {form.steps.length === 0 && (
                  <div className="text-center text-[11px] text-slate-400 py-6 border border-dashed border-slate-300 rounded-lg bg-white">
                    No steps yet. Click <strong>Add step</strong> to build the walkthrough.
                  </div>
                )}

                {form.steps.map((step, i) => {
                  const setStep = (patch) => {
                    const next = form.steps.map((s, idx) =>
                      idx === i ? { ...s, ...patch } : s
                    );
                    update({ steps: next });
                  };
                  const moveStep = (dir) => {
                    const next = [...form.steps];
                    const j = i + dir;
                    if (j < 0 || j >= next.length) return;
                    [next[i], next[j]] = [next[j], next[i]];
                    update({ steps: next });
                  };
                  const removeStep = () => {
                    if (!window.confirm(`Remove step ${i + 1}?`)) return;
                    update({ steps: form.steps.filter((_, idx) => idx !== i) });
                  };

                  return (
                    <div
                      key={i}
                      className="border border-slate-200 bg-white rounded-lg p-3 space-y-2"
                      data-testid={`admin-feature-edit-step-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-sky-600">
                          Step {i + 1}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveStep(-1)}
                            disabled={i === 0}
                            aria-label="Move up"
                            data-testid={`admin-feature-edit-step-${i}-up`}
                            className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveStep(1)}
                            disabled={i === form.steps.length - 1}
                            aria-label="Move down"
                            data-testid={`admin-feature-edit-step-${i}-down`}
                            className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={removeStep}
                            aria-label="Remove step"
                            data-testid={`admin-feature-edit-step-${i}-remove`}
                            className="p-1 rounded text-slate-500 hover:bg-red-50 hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      <Input
                        value={step.title}
                        onChange={(e) => setStep({ title: e.target.value })}
                        placeholder="Step title (e.g. Add a Presentation)"
                        data-testid={`admin-feature-edit-step-${i}-title`}
                        className="h-9 text-sm"
                      />
                      <Textarea
                        value={step.description}
                        onChange={(e) => setStep({ description: e.target.value })}
                        rows={3}
                        placeholder="Step description - plain paragraph, line breaks preserved."
                        data-testid={`admin-feature-edit-step-${i}-desc`}
                        className="text-sm"
                      />

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setMediaTarget({ type: "step", index: i })}
                          data-testid={`admin-feature-edit-step-${i}-pick-image`}
                          className="h-8 text-xs rounded-lg shrink-0"
                        >
                          <ImageIcon className="h-3 w-3 mr-1" />
                          {step.image ? "Change" : "Pick image"}
                        </Button>
                        <Input
                          value={step.image || ""}
                          onChange={(e) => setStep({ image: e.target.value || null })}
                          placeholder="…or paste image URL"
                          data-testid={`admin-feature-edit-step-${i}-image-url`}
                          className="text-xs font-mono h-8"
                        />
                      </div>

                      {step.image && (
                        <div className="rounded-lg overflow-hidden border border-slate-200 relative">
                          <img
                            src={step.image}
                            alt=""
                            className="w-full max-h-40 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => setStep({ image: null })}
                            aria-label="Clear image"
                            className="absolute top-1.5 right-1.5 bg-white/95 text-slate-500 hover:text-red-500 rounded-full p-1 shadow"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* SEO */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50">
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">SEO (detail page only)</h3>
                <div>
                  <Label className="text-xs text-slate-600">SEO title</Label>
                  <Input
                    value={form.seo_title}
                    onChange={(e) => update({ seo_title: e.target.value })}
                    placeholder="Defaults to feature name"
                    data-testid="admin-feature-edit-seotitle"
                    className="mt-1 h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Meta description</Label>
                  <Textarea
                    value={form.seo_description}
                    onChange={(e) => update({ seo_description: e.target.value })}
                    rows={2}
                    placeholder="Defaults to short description"
                    data-testid="admin-feature-edit-seodesc"
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Keywords (comma-separated)</Label>
                  <Input
                    value={form.seo_keywords}
                    onChange={(e) => update({ seo_keywords: e.target.value })}
                    placeholder="ai reporting, promo erp, dashboards"
                    data-testid="admin-feature-edit-seokw"
                    className="mt-1 h-9 text-sm"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Side column */}
        <div className="space-y-5">
          <div className="border border-slate-200 rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Visibility</h3>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => update({ published: e.target.checked })}
                data-testid="admin-feature-edit-published"
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              <span className="text-sm text-slate-700">Published (visible on /features)</span>
            </label>
          </div>

          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">Details</h3>

            <div>
              <Label className="text-xs text-slate-600">Group *</Label>
              <select
                value={form.group_slug}
                onChange={(e) => update({ group_slug: e.target.value })}
                data-testid="admin-feature-edit-group"
                className="mt-1 w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white"
              >
                <option value="">- Select -</option>
                {groups.map((g) => (
                  <option key={g.slug} value={g.slug}>{g.title}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Icon</Label>
              <div className="mt-1 flex items-center gap-2">
                <span className="h-9 w-9 shrink-0 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
                  <IconPreview className="h-4 w-4" />
                </span>
                <select
                  value={form.icon}
                  onChange={(e) => update({ icon: e.target.value })}
                  data-testid="admin-feature-edit-icon"
                  className="flex-1 h-9 px-2 text-sm border border-slate-300 rounded-md bg-white"
                >
                  {FEATURE_ICON_NAMES.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              {/* Visual icon grid */}
              <div className="mt-2 grid grid-cols-8 gap-1 max-h-40 overflow-y-auto p-1 border border-slate-200 rounded-md bg-slate-50">
                {FEATURE_ICON_NAMES.map((n) => {
                  const I = FEATURE_ICONS[n];
                  const active = form.icon === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => update({ icon: n })}
                      title={n}
                      data-testid={`admin-feature-edit-icon-${n}`}
                      className={`h-7 w-7 rounded flex items-center justify-center transition-colors ${active ? "bg-sky-600 text-white" : "text-slate-500 hover:bg-white hover:text-sky-600"}`}
                    >
                      <I className="h-3.5 w-3.5" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Display order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => update({ order: Number(e.target.value) || 0 })}
                data-testid="admin-feature-edit-order"
                className="mt-1 h-9 text-sm w-24"
              />
              <p className="text-[10px] text-slate-400 mt-1">Lower numbers appear first within the group.</p>
            </div>
          </div>
        </div>
      </div>

      <MediaPicker
        open={mediaTarget !== null}
        onClose={() => setMediaTarget(null)}
        onPick={(url) => {
          if (!mediaTarget) return;
          if (mediaTarget.type === "hero") {
            update({ hero_image: url });
          } else if (mediaTarget.type === "step") {
            const next = form.steps.map((s, idx) =>
              idx === mediaTarget.index ? { ...s, image: url } : s
            );
            update({ steps: next });
          }
          setMediaTarget(null);
        }}
      />
    </div>
  );
}
